import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { vehicleConditionAssessmentService } from '../services/api';
import type { 
  VehicleConditionAssessment, 
  VehicleConditionAssessmentFormData,
  VehicleConditionAssessmentListResponse,
  VehicleConditionAssessmentHistoryResponse,
  VehicleConditionAssessmentAfterScanResponse,
  VehicleConditionAssessmentSuggestAfterRepairResponse
} from '../types';

// State interface
export interface VehicleConditionAssessmentState {
  assessments: VehicleConditionAssessment[];
  currentAssessment: VehicleConditionAssessment | null;
  assessmentHistory: Record<string, VehicleConditionAssessment[]>;
  afterScanData: VehicleConditionAssessmentAfterScanResponse['data'] | null;
  suggestAfterRepairData: VehicleConditionAssessmentSuggestAfterRepairResponse['data'] | null;
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
}

// Initial state
const initialState: VehicleConditionAssessmentState = {
  assessments: [],
  currentAssessment: null,
  assessmentHistory: {},
  afterScanData: null,
  suggestAfterRepairData: null,
  loading: false,
  error: null,
  creating: false,
  updating: false,
  deleting: false,
};

// Async thunks
export const fetchAssessments = createAsyncThunk(
  'vehicleConditionAssessments/fetchAssessments',
  async (vehicleId: number, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.getAssessments(vehicleId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assessments');
    }
  }
);

export const fetchAssessmentById = createAsyncThunk(
  'vehicleConditionAssessments/fetchAssessmentById',
  async ({ vehicleId, assessmentId }: { vehicleId: number; assessmentId: number }, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.getAssessment(vehicleId, assessmentId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assessment');
    }
  }
);

export const createAssessment = createAsyncThunk(
  'vehicleConditionAssessments/createAssessment',
  async ({ vehicleId, data }: { vehicleId: number; data: VehicleConditionAssessmentFormData | FormData }, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.createAssessment(vehicleId, data);
      return response.data.data;
    } catch (error: any) {
      // Return full error details for better debugging
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to create assessment';
      const errorDetails = {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
        originalError: error.toString()
      };
      console.error('Error creating assessment:', errorDetails);
      return rejectWithValue(errorDetails);
    }
  }
);

export const updateAssessment = createAsyncThunk(
  'vehicleConditionAssessments/updateAssessment',
  async ({ vehicleId, assessmentId, data }: { vehicleId: number; assessmentId: number; data: Partial<VehicleConditionAssessmentFormData> }, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.updateAssessment(vehicleId, assessmentId, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update assessment');
    }
  }
);

export const deleteAssessment = createAsyncThunk(
  'vehicleConditionAssessments/deleteAssessment',
  async ({ vehicleId, assessmentId }: { vehicleId: number; assessmentId: number }, { rejectWithValue }) => {
    try {
      await vehicleConditionAssessmentService.deleteAssessment(vehicleId, assessmentId);
      return assessmentId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete assessment');
    }
  }
);

export const fetchAssessmentHistory = createAsyncThunk(
  'vehicleConditionAssessments/fetchAssessmentHistory',
  async (vehicleId: number, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.getAssessmentHistory(vehicleId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch assessment history');
    }
  }
);

export const fetchAfterScanData = createAsyncThunk(
  'vehicleConditionAssessments/fetchAfterScanData',
  async (vehicleId: number, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.getAfterScan(vehicleId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch after scan data');
    }
  }
);

export const suggestAfterRepair = createAsyncThunk(
  'vehicleConditionAssessments/suggestAfterRepair',
  async ({ vehicleId, maintenanceRecordId }: { vehicleId: number; maintenanceRecordId: number }, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.suggestAfterRepair(vehicleId, maintenanceRecordId);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to suggest after repair');
    }
  }
);

export const linkRepair = createAsyncThunk(
  'vehicleConditionAssessments/linkRepair',
  async ({ vehicleId, assessmentId, maintenanceRecordId, linkType, notes }: { 
    vehicleId: number; 
    assessmentId: number; 
    maintenanceRecordId: number; 
    linkType: 'before_repair' | 'after_repair' | 'suggested_after_repair';
    notes?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await vehicleConditionAssessmentService.linkRepair(vehicleId, assessmentId, maintenanceRecordId, linkType, notes);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to link repair');
    }
  }
);

// Slice
const vehicleConditionAssessmentSlice = createSlice({
  name: 'vehicleConditionAssessments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentAssessment: (state) => {
      state.currentAssessment = null;
    },
    clearAfterScanData: (state) => {
      state.afterScanData = null;
    },
    clearSuggestAfterRepairData: (state) => {
      state.suggestAfterRepairData = null;
    },
    setCurrentAssessment: (state, action: PayloadAction<VehicleConditionAssessment | null>) => {
      state.currentAssessment = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch assessments
    builder
      .addCase(fetchAssessments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessments.fulfilled, (state, action) => {
        state.loading = false;
        state.assessments = action.payload.data;
      })
      .addCase(fetchAssessments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch assessment by ID
    builder
      .addCase(fetchAssessmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAssessment = action.payload;
      })
      .addCase(fetchAssessmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create assessment
    builder
      .addCase(createAssessment.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.creating = false;
        state.assessments.unshift(action.payload);
        state.currentAssessment = action.payload;
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Update assessment
    builder
      .addCase(updateAssessment.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        state.updating = false;
        const index = state.assessments.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.assessments[index] = action.payload;
        }
        if (state.currentAssessment?.id === action.payload.id) {
          state.currentAssessment = action.payload;
        }
      })
      .addCase(updateAssessment.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Delete assessment
    builder
      .addCase(deleteAssessment.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        state.deleting = false;
        state.assessments = state.assessments.filter(a => a.id !== action.payload);
        if (state.currentAssessment?.id === action.payload) {
          state.currentAssessment = null;
        }
      })
      .addCase(deleteAssessment.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload as string;
      });

    // Fetch assessment history
    builder
      .addCase(fetchAssessmentHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.assessmentHistory = action.payload;
      })
      .addCase(fetchAssessmentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch after scan data
    builder
      .addCase(fetchAfterScanData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAfterScanData.fulfilled, (state, action) => {
        state.loading = false;
        state.afterScanData = action.payload;
      })
      .addCase(fetchAfterScanData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Suggest after repair
    builder
      .addCase(suggestAfterRepair.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(suggestAfterRepair.fulfilled, (state, action) => {
        state.loading = false;
        state.suggestAfterRepairData = action.payload;
      })
      .addCase(suggestAfterRepair.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Link repair
    builder
      .addCase(linkRepair.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(linkRepair.fulfilled, (state, action) => {
        state.loading = false;
        // Update current assessment if it exists
        if (state.currentAssessment) {
          if (!state.currentAssessment.repair_links) {
            state.currentAssessment.repair_links = [];
          }
          state.currentAssessment.repair_links.push(action.payload);
        }
      })
      .addCase(linkRepair.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentAssessment,
  clearAfterScanData,
  clearSuggestAfterRepairData,
  setCurrentAssessment,
} = vehicleConditionAssessmentSlice.actions;

export default vehicleConditionAssessmentSlice.reducer;


